import { useMemo } from 'react';

import type {
  ChainKey,
  ChainRuntime,
  ChainConfig,
} from '@infinex/chain-config';
import { chains, getActiveChains, isChainKey } from '@infinex/chain-config';
import type { Custody } from '@infinex/types';

import { usePlatformFeatureFlagPayload } from './featureFlags';

export type UseActiveChainKeysFilter = {
  runtime?: ChainRuntime;
  custody?: Custody;
  isMainnet?: boolean;
  applyCustomOrder?: boolean;
};

const filterByCustody = (chainKey: ChainKey, custody?: Custody): boolean => {
  if (!custody) return true;
  const chain = chains[chainKey];
  // External wallets use the same chain support as regular wallets
  if (custody.toLowerCase() === 'vault') {
    return chain.features.vault.supported;
  }
  return chain.features.wallet.supported;
};

const filterByRuntime = (
  chainKey: ChainKey,
  runtime?: ChainRuntime
): boolean => {
  if (!runtime) return true;
  return chains[chainKey].runtime === runtime;
};

const filterByReleaseStatus = (
  chainKey: ChainKey,
  upcomingChainKeys: Set<ChainKey>
): boolean => {
  const chain = chains[chainKey];
  // If chain is released, always include it
  if (chain.isReleased) return true;
  // If chain is not released, only include it if it's in the upcoming chains set
  return upcomingChainKeys.has(chainKey);
};

const filterByMainnetStatus = (
  chainKey: ChainKey,
  isMainnet?: boolean
): boolean => {
  const chain = chains[chainKey];
  if (isMainnet === undefined) return chain.isMainnet;
  return chain.isMainnet === isMainnet;
};

/**
 * Hook to get a filtered list of chain keys based on runtime, custody, and release status.
 *
 * Chains can be either released or unreleased:
 * - Released chains (isReleased: true) are always included
 * - Unreleased chains (isReleased: false) are only included if they are in the 'upcoming-chains-enabled' feature flag payload
 *
 * @example
 * // Feature flag payload should be an array of chain keys:
 * // {
 * //   "upcoming-chains-enabled": ["berachain", "blast", "sonic"]
 * // }
 *
 * // Get all active chains
 * const allChains = useActiveFrontendChainKeys();
 *
 * // Get only EVM wallet chains
 * const evmWalletChains = useActiveFrontendChainKeys({
 *   runtime: 'evm',
 *   custody: 'wallet'
 * });
 *
 * // Get chains sorted by TVL priority (as of June 2025)
 * const sortedChains = useActiveFrontendChainKeys({ applyCustomOrder: true });
 *
 * @param filter Optional filters for runtime and custody
 * @param filter.runtime - Filter by chain runtime (evm, svm, etc)
 * @param filter.custody - Filter by custody type (wallet or vault)
 * @param filter.isMainnet - Filter by mainnet or testnet DEFAULTS to true (mainnet)
 * @param filter.applyCustomOrder - Apply static ordering by TVL priority (as of June 2025)
 * @returns Filtered active chains
 */
export const useActiveFrontendChains = (
  filter?: UseActiveChainKeysFilter
): Record<ChainKey, ChainConfig> => {
  const payload = usePlatformFeatureFlagPayload('upcomingChainsEnabled');

  return useMemo(() => {
    const upcomingChainKeys = new Set<ChainKey>(
      payload?.payload?.filter(isChainKey) ?? []
    );

    const activeChains = getActiveChains();
    const filteredEntries = Object.entries(activeChains).filter(
      ([chainKey]) =>
        filterByCustody(chainKey as ChainKey, filter?.custody) &&
        filterByRuntime(chainKey as ChainKey, filter?.runtime) &&
        filterByReleaseStatus(chainKey as ChainKey, upcomingChainKeys) &&
        filterByMainnetStatus(chainKey as ChainKey, filter?.isMainnet)
    );
    const entries = filter?.applyCustomOrder
      ? filteredEntries.sort(([a], [b]) => {
          const chainA = chains[a as ChainKey];
          const chainB = chains[b as ChainKey];

          // Primary sort by orderPriority
          const priorityDiff = chainA.orderPriority - chainB.orderPriority;
          if (priorityDiff !== 0) return priorityDiff;

          // Secondary sort alphabetically by displayName if priorities are equal
          return chainA.displayName.localeCompare(chainB.displayName);
        })
      : filteredEntries;
    return Object.fromEntries(entries) as Record<ChainKey, ChainConfig>;
  }, [
    payload,
    filter?.custody,
    filter?.runtime,
    filter?.isMainnet,
    filter?.applyCustomOrder,
  ]);
};

/**
 * @see useActiveFrontendChains
 * @returns Array of chain keys that match the filters and release status
 */
export const useActiveFrontendChainKeys = (
  filter?: UseActiveChainKeysFilter
) => {
  const activeFrontendChains = useActiveFrontendChains(filter);
  return Object.keys(activeFrontendChains) as ChainKey[];
};
